import React, { useState, useEffect, useRef, useCallback, useImperativeHandle, forwardRef } from 'react';
import { Send, Loader2, Users, UserPlus, Search, X, Mic, MicOff } from 'lucide-react';
import { toast } from 'sonner';
import * as api from '../api';
import { useAuth } from '../context/AuthContext';

function firstPrenom(nom) {
  if (!nom) return '';
  const parts = nom.trim().split(' ');
  if (parts.length === 1) return parts[0];
  // Format "NOM Prénom" (majuscules) → retourner la suite
  if (parts[0] === parts[0].toUpperCase() && parts[0].length > 1) return parts.slice(1).join(' ');
  // Format "Prénom NOM" → retourner le premier mot
  return parts[0];
}

function renderMentions(text, onMentionClick, darkBg) {
  if (!text) return null;
  return text.split(/(@\S+)/g).map((part, i) =>
    /^@\S+$/.test(part)
      ? <span key={i}
          style={darkBg
            ? { fontWeight: 700, textDecoration: 'underline', cursor: onMentionClick ? 'pointer' : 'default' }
            : { fontWeight: 700, color: '#D97706', background: '#FEF3C7', borderRadius: 3, padding: '0 3px', cursor: onMentionClick ? 'pointer' : 'default' }
          }
          onClick={onMentionClick ? e => { e.stopPropagation(); onMentionClick(); } : undefined}
          title={onMentionClick ? 'Répondre à l\'auteur' : undefined}
        >{part}</span>
      : part
  );
}

/**
 * Onglet Discussions intégré dans une fiche.
 * Affichage direct de l'interface de chat.
 * Si aucun thread n'existe, le premier message le crée automatiquement.
 *
 * Props:
 *   record_type  : 'entreprise'|'vente'|'achat'|'location'|'atelier'
 *   record_id    : string
 *   record_label : string
 */
export const DiscussionsFicheTab = forwardRef(function DiscussionsFicheTab({ record_type, record_id, record_label }, ref) {
  const { user } = useAuth();
  const [thread,  setThread]  = useState(null);
  const [loading, setLoading] = useState(true);
  const [reply,   setReply]   = useState('');
  const [sending, setSending] = useState(false);
  const [mentions,      setMentions]      = useState([]);   // [{user_id, user_nom}]
  const [mentionState,  setMentionState]  = useState(null); // { query } | null
  const [allUsers,          setAllUsers]          = useState([]);
  const [pendingParticipants, setPendingParticipants] = useState([]); // contacts ajoutés avant création du thread
  const [inviteOpen,  setInviteOpen]  = useState(false);
  const [inviteQuery, setInviteQuery] = useState('');
  const [invitePos,   setInvitePos]   = useState(null);
  const bottomRef  = useRef(null);
  const inputRef   = useRef(null);
  const inviteBtnRef = useRef(null);
  const recogRef   = useRef(null);
  const [listening, setListening] = useState(false);

  const startVoice = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { toast.error('Reconnaissance vocale non supportée sur ce navigateur'); return; }
    const rec = new SR();
    rec.lang = 'fr-FR';
    rec.interimResults = false;
    rec.onresult = (e) => {
      const t = e.results[0][0].transcript;
      setReply(prev => prev ? `${prev} ${t}` : t);
    };
    rec.onend = () => setListening(false);
    rec.onerror = () => { setListening(false); toast.error('Erreur micro'); };
    rec.start();
    recogRef.current = rec;
    setListening(true);
  };

  const stopVoice = () => {
    recogRef.current?.stop();
    setListening(false);
  };

  // Charger tous les utilisateurs pour l'invitation
  useEffect(() => {
    api.getUsers().then(r => setAllUsers(r.data || [])).catch(() => {});
  }, []);

  // Fermer le dropdown au clic extérieur
  useEffect(() => {
    const h = (e) => {
      if (!inviteOpen) return;
      if (inviteBtnRef.current?.contains(e.target)) return;
      if (e.target.closest?.('[data-fiche-invite-dropdown]')) return;
      setInviteOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [inviteOpen]);

  // Marquer lu à chaque fois que le composant devient visible (onglet Activité cliqué)
  useEffect(() => {
    if (thread?.thread_id) {
      api.markDiscussionRead(thread.thread_id).catch(() => {});
    }
  }, [thread?.thread_id]);

  // Expose focusInput pour le bouton + du ColHeader parent
  useImperativeHandle(ref, () => ({
    focusInput: () => {
      inputRef.current?.focus();
      inputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    },
  }));

  const load = useCallback(async () => {
    if (!record_id) return;
    setLoading(true);
    try {
      const r = await api.getDiscussionByRecord(record_type, record_id);
      setThread(r.data || null);
      // Marquer comme lu dès l'ouverture si un thread existe
      if (r.data?.thread_id) {
        api.markDiscussionRead(r.data.thread_id).catch(() => {});
      }
    } catch {
      setThread(null);
    } finally {
      setLoading(false);
    }
  }, [record_type, record_id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [thread?.messages?.length]);

  const insertMentionInFiche = (u) => {
    const text   = reply;
    const el     = inputRef.current;
    const cursor = el?.selectionStart ?? text.length;
    const before = text.slice(0, cursor);
    const atIdx  = before.lastIndexOf('@');
    const prenom = firstPrenom(u.user_nom || u.name || '');
    const newText = text.slice(0, atIdx) + `@${prenom} ` + text.slice(cursor);
    setReply(newText);
    setMentions(prev => [...prev.filter(m => m.user_id !== u.user_id), { user_id: u.user_id, user_nom: u.user_nom || u.name }]);
    setMentionState(null);
    setTimeout(() => {
      if (el) { el.focus(); const p = atIdx + prenom.length + 2; el.setSelectionRange(p, p); }
    }, 10);
  };

  const handleSend = async () => {
    const text = reply.trim();
    if (!text) return;
    // Validation mentions : garder seulement si @Prénom encore présent dans le texte
    const validMentions = mentions.filter(m => {
      const prenom = firstPrenom(m.user_nom || '');
      return prenom && text.includes(`@${prenom}`);
    });
    setSending(true);
    try {
      if (!thread) {
        // Créer le thread avec le premier message + participants en attente + mentionnés
        const mentionIds = validMentions.map(m => m.user_id).filter(Boolean);
        const allParticipantIds = [...new Set([...pendingParticipants.map(u => u.user_id), ...mentionIds])];
        await api.createDiscussion({ record_type, record_id, record_label, first_message: text, participant_ids: allParticipantIds });
        setPendingParticipants([]);
        setReply('');
        setMentions([]);
        await load();
      } else {
        const r = await api.postDiscussionMessage(thread.thread_id, text, validMentions);
        setThread(prev => ({ ...prev, messages: [...(prev.messages || []), r.data] }));
        setReply('');
        setMentions([]);
        // Rafraîchir la liste des participants si de nouveaux ont été mentionnés
        if (validMentions.length > 0) await load();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur envoi');
    } finally {
      setSending(false);
    }
  };

  const handleInvite = async (u) => {
    if (!thread?.thread_id) {
      // Pas encore de thread : stocker localement
      if (!pendingParticipants.find(p => p.user_id === u.user_id)) {
        setPendingParticipants(prev => [...prev, u]);
      }
      setInviteOpen(false);
      return;
    }
    try {
      await api.inviteToDiscussion(thread.thread_id, [u.user_id]);
      await load();
      setInviteOpen(false);
      toast.success('Participant ajouté');
    } catch { toast.error('Erreur invitation'); }
  };

  const messages     = thread?.messages     || [];
  const participants = thread ? (thread?.participants || []) : pendingParticipants;
  const userId       = user?.user_id        || '';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, padding: '0 12px' }}>

      {/* Zone messages */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '8px 0', display: 'flex', flexDirection: 'column', gap: 8 }}>
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 size={18} className="animate-spin" style={{ color: 'var(--text-muted)' }} />
          </div>
        ) : messages.length === 0 ? (
          <p style={{ textAlign: 'center', fontSize: 12, color: 'var(--text-muted)', paddingTop: 24 }}>
            Aucun message — écrivez le premier
          </p>
        ) : (
          messages.map((msg, i) => {
            const isMe   = msg.auteur_id === userId;
            const prev   = messages[i - 1];
            const isFirst = !prev || prev.auteur_id !== msg.auteur_id;
            return (
              <div key={msg.message_id}
                style={{ display: 'flex', flexDirection: 'column', alignItems: isMe ? 'flex-end' : 'flex-start', gap: 2, padding: '0 2px', marginTop: isFirst && i > 0 ? 8 : 0 }}>
                {isFirst && (
                  <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                    {isMe ? 'Vous' : (
                      <>
                        <span className="sm:hidden">{firstPrenom(msg.auteur_nom)}</span>
                        <span className="hidden sm:inline">{msg.auteur_nom}</span>
                      </>
                    )} · {formatTime(msg.created_at)}
                  </span>
                )}
                <div style={{
                  maxWidth: '82%', padding: '7px 11px', fontSize: 13, lineHeight: 1.5, wordBreak: 'break-word',
                  borderRadius: isMe
                    ? (isFirst ? '12px 12px 2px 12px' : '12px 2px 2px 12px')
                    : (isFirst ? '12px 12px 12px 2px' : '2px 12px 12px 2px'),
                  background: isMe ? '#1D4ED8' : 'var(--bg-muted, #F3F4F6)',
                  color: isMe ? 'white' : 'var(--text)',
                }}>
                  {renderMentions(msg.corps, !isMe ? () => {
                    const prenom = firstPrenom(msg.auteur_nom || '');
                    if (prenom) {
                      setReply(`@${prenom} `);
                      setMentions([{ user_id: msg.auteur_id, user_nom: msg.auteur_nom }]);
                      setTimeout(() => inputRef.current?.focus(), 10);
                    }
                  } : null, isMe)}
                </div>
              </div>
            );
          })
        )}
        <div ref={bottomRef} />
      </div>

      {/* Participants + bouton Ajouter */}
      <div style={{ padding: '5px 2px', display: 'flex', alignItems: 'center', gap: 5, borderTop: '1px solid var(--border-light)', minHeight: 28 }}>
        <Users size={11} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
        <span style={{ fontSize: 11, color: 'var(--text-muted)', flex: 1, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {participants.length > 0 ? participants.map((p, i) => (
            <span key={p.user_id || i} style={{ background: '#EFF6FF', color: '#1D4ED8', borderRadius: 999, padding: '1px 8px', fontSize: 11, fontWeight: 500, whiteSpace: 'nowrap' }}>
              {p.user_nom || p.name || p.email}
            </span>
          )) : <em>Aucun participant</em>}
        </span>
        <div style={{ position: 'relative', flexShrink: 0 }}>
            <button
              ref={inviteBtnRef}
              onClick={() => {
                if (inviteOpen) { setInviteOpen(false); return; }
                const rect = inviteBtnRef.current?.getBoundingClientRect();
                if (rect) setInvitePos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
                setInviteOpen(true);
                setInviteQuery('');
              }}
              data-testid="fiche-invite-btn"
              style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '3px 10px', borderRadius: 6, border: '1px solid #BFDBFE', background: 'white', color: '#1D4ED8', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
              <UserPlus size={11} /> Ajouter
            </button>
            {inviteOpen && invitePos && (
              <div data-fiche-invite-dropdown style={{ position: 'fixed', top: invitePos.top, right: invitePos.right, zIndex: 9999, background: 'white', border: '1px solid var(--border)', borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', width: 220 }}>
                <div style={{ padding: '6px 8px', borderBottom: '1px solid var(--border-light)', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Search size={11} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                  <input autoFocus value={inviteQuery} onChange={e => setInviteQuery(e.target.value)}
                    placeholder="Rechercher…"
                    style={{ border: 'none', outline: 'none', fontSize: 12, flex: 1, background: 'transparent', color: 'var(--text)' }} />
                  <button onClick={() => setInviteOpen(false)} style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 0 }}><X size={11} style={{ color: 'var(--text-muted)' }} /></button>
                </div>
                <div style={{ maxHeight: 160, overflowY: 'auto' }}>
                  {allUsers.filter(u => u.actif !== false && (u.name || u.email || '').toLowerCase().includes(inviteQuery.toLowerCase())).map(u => (
                    <div key={u.user_id}
                      onClick={() => handleInvite(u)}
                      style={{ padding: '7px 12px', fontSize: 12, cursor: 'pointer', borderBottom: '1px solid var(--border-light)' }}
                      onMouseEnter={ev => ev.currentTarget.style.background = '#EFF6FF'}
                      onMouseLeave={ev => ev.currentTarget.style.background = 'transparent'}>
                      {u.name || u.email}
                    </div>
                  ))}
                  {allUsers.filter(u => u.actif !== false && (u.name || u.email || '').toLowerCase().includes(inviteQuery.toLowerCase())).length === 0 && (
                    <div style={{ padding: '8px 12px', fontSize: 11, color: 'var(--text-muted)' }}>Aucun résultat</div>
                  )}
                </div>
              </div>
            )}
          </div>
      </div>

      {/* Zone de saisie avec @mention */}
      <div style={{ borderTop: '1px solid var(--border)', paddingTop: 8, position: 'relative' }}>
        {/* Dropdown @mention */}
        {mentionState && (
          <div style={{ position: 'absolute', bottom: '100%', left: 0, right: 44, zIndex: 9999, marginBottom: 3, background: 'white', border: '1px solid #FDE68A', borderRadius: 8, boxShadow: '0 -4px 16px rgba(0,0,0,0.1)', maxHeight: 150, overflowY: 'auto' }}>
            {allUsers
              .filter(u => u.actif !== false && firstPrenom(u.name || u.email || '').toLowerCase().includes(mentionState.query.toLowerCase()))
              .slice(0, 5)
              .map(u => (
                <div key={u.user_id}
                  onMouseDown={e => { e.preventDefault(); insertMentionInFiche({ user_id: u.user_id, user_nom: u.name || u.email }); }}
                  style={{ padding: '6px 12px', fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, borderBottom: '1px solid #FEF3C7' }}
                  onMouseEnter={ev => ev.currentTarget.style.background = '#FEF3C7'}
                  onMouseLeave={ev => ev.currentTarget.style.background = 'transparent'}>
                  <span style={{ fontWeight: 800, color: '#D97706' }}>@</span>
                  <span style={{ fontWeight: 600 }}>{firstPrenom(u.name || u.email || '')}</span>
                  <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 'auto' }}>{u.name || u.email}</span>
                </div>
              ))
            }
            {allUsers.filter(u => u.actif !== false && firstPrenom(u.name || u.email || '').toLowerCase().includes((mentionState.query || '').toLowerCase())).length === 0 && (
              <div style={{ padding: '8px 12px', fontSize: 11, color: 'var(--text-muted)' }}>Aucun utilisateur</div>
            )}
          </div>
        )}
        <div style={{ display: 'flex', gap: 7, alignItems: 'flex-end' }}>
          <div style={{ flex: 1, position: 'relative' }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 4 }}>
              <button
                type="button"
                onClick={listening ? stopVoice : startVoice}
                data-testid="fiche-discussion-mic-btn"
                style={{
                  display: 'flex', alignItems: 'center', gap: 5, padding: '3px 10px',
                  borderRadius: 6, border: `1px solid ${listening ? '#FECACA' : '#E2E8F0'}`,
                  background: listening ? '#FEE2E2' : '#F1F5F9',
                  color: listening ? '#B91C1C' : '#475569',
                  fontSize: 12, fontWeight: 600, cursor: 'pointer',
                }}>
                {listening ? <MicOff size={12} /> : <Mic size={12} />}
                {listening ? 'Arrêter' : 'Dicter'}
              </button>
            </div>
            <textarea value={reply}
              onChange={e => {
                const val = e.target.value;
                setReply(val);
                const cursor = e.target.selectionStart;
                const match = val.slice(0, cursor).match(/@(\w*)$/);
                if (match) setMentionState({ query: match[1] });
                else setMentionState(null);
              }}
              ref={inputRef}
              onKeyDown={e => {
                if (e.key === 'Escape') setMentionState(null);
                if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) handleSend();
              }}
              onBlur={() => setTimeout(() => setMentionState(null), 150)}
              placeholder="Message… (@nom pour mentionner — Ctrl+Entrée)" rows={2}
              style={{ flex: 1, width: '100%', border: '1px solid var(--border)', borderRadius: 8, padding: '6px 10px', fontSize: 13, resize: 'none', outline: 'none', lineHeight: 1.5, background: 'var(--surface)', color: 'var(--text)', boxSizing: 'border-box' }}
              data-testid="fiche-discussion-reply-input" />
          </div>
          <button onClick={handleSend} disabled={sending || !reply.trim()} data-testid="fiche-discussion-send-btn"
            style={{ padding: '8px 11px', borderRadius: 8, border: 'none', background: reply.trim() ? '#1D4ED8' : 'var(--border)', color: reply.trim() ? 'white' : 'var(--text-muted)', cursor: reply.trim() ? 'pointer' : 'default', display: 'flex', alignItems: 'center', transition: 'background 0.15s' }}>
            {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
          </button>
        </div>
      </div>
    </div>
  );
});


function formatTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = (Date.now() - d) / 1000;
  if (diff < 60)    return 'à l\'instant';
  if (diff < 3600)  return `il y a ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `il y a ${Math.floor(diff / 3600)} h`;
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
}
